import { postApi } from './apiServices';
import { secureStorage } from '../Utils/encryption';

/**
 * Admin login service
 * @param {Object} credentials - Login credentials { email, password }
 * @returns {Promise} Login response
 */
export const adminLogin = async (credentials) => {
  try {
    const response = await postApi('/api/admin-login', credentials, {
      showSuccessToast: false
    });
    return response;
  } catch (error) {
    // For login, handle 401 responses as normal responses to show API error messages
    if (error.response?.status === 401 && error.response?.data) {
      return error.response.data;
    }
    console.error('Admin login error:', error);
    throw error;
  }
};

/**
 * Admin logout service
 * @returns {Promise} Logout response
 */
export const adminLogout = async () => {
  try {
    // Call logout API if needed
    await postApi('/admin-logout', {}, { showSuccessToast: false });
  } catch (error) {
    console.error('Logout API error:', error);
    // Continue with local logout even if API fails
  } finally {
    // Clear local storage
    secureStorage.removeItem('token');
    secureStorage.removeItem('adminUser');
    secureStorage.removeItem('frontend_version');
  }
};

/**
 * Check if admin is authenticated
 * @returns {boolean} Authentication status
 */
export const isAdminAuthenticated = () => {
  const token = secureStorage.getItem('token');
  const adminUser = secureStorage.getItem('adminUser');
  return !!(token && adminUser);
};

/**
 * Get current admin user data
 * @returns {Object|null} Admin user data
 */
export const getCurrentAdminUser = () => {
  return secureStorage.getItem('adminUser');
};

/**
 * Get admin token
 * @returns {string|null} Admin token
 */
export const getAdminToken = () => {
  return secureStorage.getItem('token');
};

/**
 * Set admin authentication data
 * @param {Object} authData - Authentication data { token, user }
 */
export const setAdminAuthData = (authData) => {
  if (authData.token) {
    secureStorage.setItem('token', authData.token);
  }
  if (authData.user) {
    secureStorage.setItem('adminUser', authData.user);
  }
};

/**
 * Clear admin authentication data
 */
export const clearAdminAuthData = () => {
  secureStorage.removeItem('token');
  secureStorage.removeItem('adminUser');
};

/**
 * Forgot password service
 * @param {Object} data - Forgot password data { email }
 * @returns {Promise} Forgot password response
 */
export const forgotPassword = async (data) => {
  try {
    const response = await postApi('/api/forgot-password', data, {
      showSuccessToast: true
    });
    return response;
  } catch (error) {
    console.error('Forgot password error:', error);
    throw error;
  }
};

/**
 * Refresh admin token (if needed)
 * @returns {Promise} Token refresh response
 */
export const refreshAdminToken = async () => {
  try {
    const response = await postApi('/admin-refresh-token', {}, {
      showSuccessToast: false
    });
    
    if (response.statusCode === 200 && response.data.token) {
      secureStorage.setItem('token', response.data.token);
      return response.data.token;
    }
    
    return null;
  } catch (error) {
    console.error('Token refresh error:', error);
    // If refresh fails, clear auth data
    clearAdminAuthData();
    throw error;
  }
}; 